import React from "react";
import { CheckCircle } from "@mui/icons-material";
import CancelIcon from "@mui/icons-material/Cancel";
import { Box, Button, Divider, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import "./PaymentStatusCard.scss";
import { directPayment } from "../../../api/payment.api";
import { formatTransactionDate } from "../../../utils/utils";

interface PaymentStatusCardProps {
  paymentStatus: boolean;
  details: any;
  paymentIntentId?: any;
  isOrderSuccess?: boolean;
}

const PaymentStatusCard = ({
  paymentStatus,
  details,
  paymentIntentId,
  isOrderSuccess,
}: PaymentStatusCardProps) => {
  const navigate = useNavigate();

  const handleRetryPayment = async () => {
    try {
      const body = {
        orderId: details.orderId,
        amount: details.grandTotal,
        paymentType: details.paymentType,
      };

      const response = await directPayment(body);

      const newClientSecret = response?.data?.data?.data?.clientSecret;

      if (!newClientSecret) {
        console.error("Failed to get clientSecret for retry");
        return;
      }

      navigate("/order-summary/payment", {
        state: {
          clientSecret: newClientSecret,
          orderId: details.orderId,
          grandTotal: details.grandTotal,
        },
      });
    } catch (err) {
      console.error("Retry Payment Error:", err);
    }
  };

  const handleViewOrderHistory = () => {
    navigate("/order-history");
  };

  const { grandTotal, last4, brand, paymentType, transactionDate, txnId } =
    details || {};

  return (
    <Box className="payment-success-card">
      {paymentStatus ? (
        <CheckCircle className="payment-success-card__icon" />
      ) : (
        <CancelIcon
          color="error"
          className="payment-success-card__failure-icon"
        />
      )}

      <Typography variant="h5" className="payment-success-card__title">
        {paymentStatus
          ? isOrderSuccess
            ? "Order Successful"
            : "Payment Successful"
          : "Payment Unsuccessful"}
      </Typography>

      <Typography variant="body1" className="payment-success-card__message">
        {paymentStatus
          ? isOrderSuccess
            ? `Thank you for your order. Your order #${txnId} is being processed.`
            : `Thank you for your payment. Your order #${txnId} is being processed.`
          : `Your payment for order #${paymentIntentId} could not be completed.`}
      </Typography>

      {!paymentStatus && details?.errorMessage && (
        <Typography
          className="payment-success-card__reason"
          style={{ marginTop: "8px" }}
        >
          Reason for payment failure: {details?.errorMessage}
        </Typography>
      )}

      <Divider className="payment-success-card__divider" />

      {!isOrderSuccess && (
        <Box className="payment-success-card__details">
          <Box className="payment-success-card__row">
            <Typography className="payment-success-card__label">
              Amount Paid
            </Typography>
            <Typography className="payment-success-card__value">
              $ {grandTotal ?? 0}.00
            </Typography>
          </Box>

          <Box className="payment-success-card__row">
            <Typography className="payment-success-card__label">
              Payment Method
            </Typography>
            <Typography className="payment-success-card__value">
              {brand
                ? `${brand.charAt(0).toUpperCase() + brand.slice(1)} ending in ${last4}`
                : paymentType === "credit_card"
                  ? "Credit Card"
                  : "Debit Card"}
            </Typography>
          </Box>

          <Box className="payment-success-card__row">
            <Typography className="payment-success-card__label">
              Date & Time
            </Typography>
            <Typography className="payment-success-card__value">
              {transactionDate ? formatTransactionDate(transactionDate) : "--"}
            </Typography>
          </Box>
        </Box>
      )}

      <Box className="payment-success-card__actions">
        <Button
          variant="outlined"
          className="payment-success-card__button payment-success-card__button--home"
          onClick={() => navigate("/home")}
        >
          Return to Home
        </Button>
        <Button
          className="payment-success-card__button payment-success-card__button--history"
          onClick={handleViewOrderHistory}
        >
          View Order History
        </Button>
      </Box>

      {!paymentStatus && (
        <Button
          onClick={handleRetryPayment}
          className="payment-success-card__button payment-success-card__button--history"
          sx={{ mt: 2 }}
        >
          Retry Payment
        </Button>
      )}
    </Box>
  );
};

export default PaymentStatusCard;
